import type { CodeableConcept, Resource } from '@medplum/fhirtypes';
import format from 'date-fns/format';
import parseISO from 'date-fns/parseISO';

import { Caption, Stack, Text, cn } from '@eluve/components';

import { diffResourceOps } from '../core/edit-store';

/**
 * Version timeline for one resource: give it the versions from a FHIR
 * `_history` read (any order) and it renders what changed between each,
 * newest first. Presentation only — fetching history is the app's job
 * (REST `_history`; the FHIR GraphQL spec does not cover it). Change
 * detection reuses diffResourceOps, so "what changed" here is exactly
 * what a commit would have patched.
 */
export type FhirVersionHistoryProps = {
  versions: Resource[];
};

function versionTime(resource: Resource): string | undefined {
  return resource.meta?.lastUpdated;
}

function timeText(value: string | undefined): string {
  if (!value) return 'Unknown time';
  try {
    return format(parseISO(value), 'MMM d, yyyy h:mm a');
  } catch {
    return value;
  }
}

function isCodeableConcept(value: unknown): value is CodeableConcept {
  if (!value || typeof value !== 'object') return false;
  return 'coding' in value || 'text' in value;
}

/** Short human summary of one field value — codes over structure dumps. */
function summarizeValue(value: unknown): string {
  if (value === undefined || value === null) return 'none';
  if (typeof value === 'string' || typeof value === 'number') {
    return String(value);
  }
  if (typeof value === 'boolean') return value ? 'yes' : 'no';
  if (Array.isArray(value)) {
    if (value.length === 0) return 'none';
    const parts = value.map(summarizeValue).filter((part) => part !== '…');
    return parts.length > 0 ? parts.join(', ') : 'updated';
  }
  if (isCodeableConcept(value)) {
    return (
      value.text ?? value.coding?.[0]?.display ?? value.coding?.[0]?.code ?? '…'
    );
  }
  if (typeof value === 'object' && 'text' in value) {
    const text = (value as { text?: unknown }).text;
    if (typeof text === 'string') return text;
  }
  return '…';
}

/** `/clinicalStatus` → `clinical status`. */
function fieldLabel(path: string): string {
  return path
    .replace(/^\//, '')
    .replace(/([a-z])([A-Z])/g, '$1 $2')
    .toLowerCase();
}

type ChangeLine = { label: string; before: string; after: string };

function changesBetween(prev: Resource, next: Resource): ChangeLine[] {
  return diffResourceOps(prev, next)
    .filter((op) => op.op !== 'test')
    .map((op) => {
      const key = op.path.replace(/^\//, '');
      const before = summarizeValue(
        (prev as unknown as Record<string, unknown>)[key],
      );
      const after =
        op.op === 'remove'
          ? 'none'
          : summarizeValue((op as { value?: unknown }).value);
      return { label: fieldLabel(op.path), before, after };
    });
}

export function FhirVersionHistory({ versions }: FhirVersionHistoryProps) {
  const ordered = [...versions].sort((a, b) =>
    (versionTime(b) ?? '').localeCompare(versionTime(a) ?? ''),
  );
  if (ordered.length === 0) {
    return (
      <Text size="sm" color="tertiary" data-view="fhir-version-history">
        No history recorded.
      </Text>
    );
  }
  return (
    <ol data-view="fhir-version-history" className="flex flex-col">
      {ordered.map((version, index) => {
        const previous = ordered[index + 1];
        const isCreation = !previous;
        const changes = previous ? changesBetween(previous, version) : [];
        const isLastRow = index === ordered.length - 1;
        return (
          <li
            key={version.meta?.versionId ?? index}
            className="grid grid-cols-[auto_1fr] gap-x-3"
          >
            {/* Timeline gutter: node dot + connector down to the next event. */}
            <Stack align="center">
              <div
                className={cn(
                  'mt-1.5 size-2 shrink-0 rounded-full',
                  isCreation
                    ? 'border border-border-primary bg-bg-tertiary'
                    : 'bg-text-primary',
                )}
              />
              {!isLastRow && <div className="w-px flex-1 bg-bg-tertiary" />}
            </Stack>
            <div className={cn('flex min-w-0 flex-col', !isLastRow && 'pb-4')}>
              <Stack
                direction="horizontal"
                gap={3}
                align="baseline"
                justify="between"
              >
                <Text size="sm" weight="semibold">
                  {isCreation ? 'Created' : 'Updated'}
                </Text>
                <Caption
                  color="tertiary"
                  className="whitespace-nowrap privacy-text"
                >
                  {timeText(versionTime(version))}
                </Caption>
              </Stack>
              {!isCreation &&
                (changes.length === 0 ? (
                  <Text size="sm" color="tertiary">
                    No visible changes
                  </Text>
                ) : (
                  changes.map((change) => (
                    <Text key={change.label} size="sm" color="secondary">
                      <span className="capitalize">{change.label}</span>
                      {': '}
                      <span className="text-text-tertiary privacy-text">
                        {change.before}
                      </span>
                      <span className="text-text-tertiary"> → </span>
                      <span className="font-medium text-text-primary privacy-text">
                        {change.after}
                      </span>
                    </Text>
                  ))
                ))}
            </div>
          </li>
        );
      })}
    </ol>
  );
}
